import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { products } from '../data/products';
import { categories } from '../data/categories';
import SEO from '../common/SEO';
import NotFound from './NotFound';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';
import ProductGallery from '../components/ProductGallery';

const ProductPage = () => {
  const { kategorie, slug } = useParams();
  const product = products.find(p => p.slug === slug && p.kategorie === kategorie);
  const category = categories.find(c => c.slug === kategorie);

  if (!product) return <NotFound />;

  const docLabels = {
    specifikace: "Specifikace",
    certifikace: "Certifikace",
    navod: "Návod"
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.nazev,
    "description": product.perex,
    "brand": { "@type": "Brand", "name": "LightWell" },
    ...(product.obrazky?.length ? { "image": product.obrazky[0] } : {}),
    "additionalProperty": product.specifikace.map(s => ({
      "@type": "PropertyValue",
      "name": s.parametr,
      "value": s.hodnota
    }))
  };

  return (
    <>
      <SEO 
        title={product.seo.title}
        description={product.seo.description}
        path={`/produkty/${kategorie}/${slug}`}
        schema={schema}
      />
      <div className="pt-20">
        <div className="bg-gray-50 py-4 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-400">
            <Link to="/" className="hover:text-blue-600 transition-colors">Úvod</Link>
            <span>/</span>
            <Link to={`/produkty/${kategorie}`} className="hover:text-blue-600 transition-colors">{category?.nazev || 'Produkty'}</Link>
            <span>/</span>
            <span className="text-gray-900">{product.nazev}</span>
          </div>
        </div>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div>
                <ProductGallery images={product.obrazky} alt={product.nazev} />
              </div>

              <div className="flex flex-col">
                <h1 className="text-4xl md:text-5xl font-bold italic uppercase tracking-tighter mb-6 text-gray-900 leading-tight">{product.nazev}</h1>
                <p className="text-xl text-gray-600 mb-10 leading-relaxed flex-grow">{product.perex}</p>

                {/* Dokumenty ke stažení - Renderuje se pouze pokud existují dokumenty */}
                {product.dokumenty && product.dokumenty.length > 0 && (
                  <div className="mb-10">
                    <h3 className="text-lg font-bold uppercase tracking-tight mb-6 flex items-center gap-3 text-gray-900">
                      <SafeIcon name="Download" className="text-blue-600" />
                      Dokumenty ke stažení
                    </h3>
                    <div className="grid grid-cols-1 gap-3">
                      {product.dokumenty.map((doc, i) => (
                        <a key={i} href={doc.soubor} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-5 bg-gray-50 rounded-2xl border border-gray-100 hover:border-blue-600 hover:bg-blue-50 transition-all group">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-blue-600 shadow-sm"><SafeIcon name="FileText" /></div>
                            <div>
                              <div className="text-sm font-bold text-gray-900">{doc.nazev}</div>
                              <div className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">{docLabels[doc.typ] || doc.typ}</div>
                            </div>
                          </div>
                          <SafeIcon name="ChevronRight" className="text-gray-300 group-hover:text-blue-600 transition-colors" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4 mt-auto">
                  <Link to="/kontakt" className="flex-1 py-5 bg-blue-600 text-white rounded-2xl font-bold uppercase tracking-widest text-center hover:bg-blue-700 transition-all shadow-xl shadow-blue-600/20">Poptat řešení</Link>
                  <Link to={`/produkty/${kategorie}`} className="flex-1 py-5 border-2 border-gray-900 text-gray-900 rounded-2xl font-bold uppercase tracking-widest text-center hover:bg-gray-900 hover:text-white transition-all">Zpět na přehled</Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50 border-t border-gray-100">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold italic uppercase tracking-tighter mb-12 text-center">Kompletní technický list</h2>
            <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-xl border border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
                {product.specifikace.map((s, i) => (
                  <div key={i} className="flex justify-between items-center py-4 border-b border-gray-50 last:border-0">
                    <span className="text-[10px] font-bold uppercase text-gray-400 tracking-widest">{s.parametr}</span>
                    <span className="text-sm font-bold text-gray-900">{s.hodnota}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProductPage;